/**
 * The fields the classification actually reads. Declared structurally rather
 * than as TenantSummary so the accountant's client-detail header — a different
 * payload carrying the same three fields — classifies through this one
 * function instead of growing a second copy of the rules.
 */
export interface TenantRegimeInput {
  ivaRegime: string | null;
  isMonotributo: boolean;
  companyType: string | null;
}

/**
 * The fiscal regime an accountant needs to see at a glance in the cartera.
 *
 * NOT the subscription plan (Tala / Ombú / Ceibo) — that is billing. This is
 * what the client files under.
 *
 * The authoritative source is the tenant's FISCAL PROFILE, not the company
 * record. An earlier version of this read `company.literalE`, an onboarding
 * flag that is unset for essentially every tenant, so the whole cartera
 * reported one regime. The real fields, per fiscal-profile.entity.ts:
 *
 * - `ivaRegime` — how they file IVA, and the regime name follows from it:
 *     'minimum'              -> Literal E / Pequeña Empresa (fixed IVA mínimo)
 *     'cede'                 -> CEDE      (DGI calendar Ordinal 2)
 *     'no_cede'              -> NO CEDE   (Ordinal 3)
 *     'servicios_personales' -> independent professionals, bimonthly
 * - `taxes` containing MONOTRIBUTO — the BPS unified regime, which
 *   "substitutes IVA + IRAE, so it never coexists with them on a profile".
 *   It therefore arrives here as its own flag rather than as an ivaRegime.
 * - `companyType` — self-declared at the verify-company onboarding step, long
 *   before any profile is derived. It is the ONLY Monotributo signal for a
 *   tenant whose fiscal profile does not exist yet, which is why reading the
 *   derived profile alone reported no monotributistas at all.
 *
 * There is deliberately no "régimen general" bucket: DGI does not have one,
 * and inventing it made every unclassified tenant look classified.
 */

export type TenantRegimeKind =
  | "monotributo"
  | "literal-e"
  | "cede"
  | "no-cede"
  | "servicios-personales"
  | "unknown";

const IVA_REGIME_TO_KIND: Record<string, TenantRegimeKind> = {
  minimum: "literal-e",
  cede: "cede",
  no_cede: "no-cede",
  servicios_personales: "servicios-personales",
};

/**
 * Monotributo takes precedence because it substitutes IVA outright — a
 * profile carrying both is contradictory, and the tax type is the stronger
 * statement.
 *
 * Returns "unknown" when the fiscal profile has not been derived yet. That is
 * a real and common state, and it must read as "we don't know" rather than
 * being folded into whichever regime is most common — an accountant acting on
 * a wrong regime is worse off than one who can see the gap.
 */
export function describeTenantRegime(
  tenant: TenantRegimeInput,
): TenantRegimeKind {
  // Monotributo is checked from BOTH sources before any ivaRegime, because
  // the two cannot legitimately coexist: Monotributo substitutes IVA + IRAE
  // outright, so a monotributista carrying an ivaRegime is contradictory data
  // rather than a genuine dual state.
  //
  // An earlier version consulted the derived ivaRegime first on the principle
  // that the derived profile outranks a self-declaration. That is right in
  // general and wrong here: real monotributistas were being reported as
  // Literal E, because their profile still carried ivaRegime='minimum' while
  // companyType said monotributo. When the two disagree about THIS
  // distinction, the declaration is the reliable half.
  if (tenant.isMonotributo || tenant.companyType === "monotributo") {
    return "monotributo";
  }
  if (tenant.ivaRegime == null) return "unknown";
  return IVA_REGIME_TO_KIND[tenant.ivaRegime] ?? "unknown";
}

/** The four legal forms. Deliberately NOT the five values `company.companyType`
 *  carries: that column adds `monotributo`, which is a REGIME, not a form. */
export type TenantLegalForm = "unipersonal" | "sas" | "srl" | "sa";

const LEGAL_FORMS: readonly string[] = ["unipersonal", "sas", "srl", "sa"];

/**
 * The legal form to display, from the two places it is recorded.
 *
 * `fiscal_profile.companyType` wins: its enum is constrained to the four real
 * forms, and it is derived rather than self-declared. `company.companyType` is
 * the fallback for a tenant whose profile has not been derived yet — a common
 * state, not an edge case.
 *
 * A fallback value of `monotributo` yields NULL, because monotributo is not a
 * legal form. The regime carries that fact instead; returning it here would
 * put a tax regime under a "Tipo de empresa" label.
 */
export function resolveTenantLegalForm(
  profileCompanyType: string | null | undefined,
  companyCompanyType: string | null | undefined,
): TenantLegalForm | null {
  for (const candidate of [profileCompanyType, companyCompanyType]) {
    const value = (candidate ?? "").trim();
    if (LEGAL_FORMS.includes(value)) return value as TenantLegalForm;
  }
  return null;
}

/**
 * i18n `[key, defaultValue]` for each REAL regime.
 *
 * `unknown` is deliberately absent. It is not a regime, and the call sites
 * render the gap differently ON PURPOSE: the cartera table shows a muted dash
 * (a blank cell would read as missing data), the client-detail header shows no
 * badge at all, and settings shows its "no proporcionado" treatment. Folding
 * those into one shared string would flatten three considered decisions into
 * whichever happened to be written first.
 */
export const REGIME_LABEL: Record<
  Exclude<TenantRegimeKind, "unknown">,
  [string, string]
> = {
  monotributo: ["accountant.regime.monotributo", "Monotributo"],
  "literal-e": ["accountant.regime.literalE", "Literal E"],
  cede: ["accountant.regime.cede", "CEDE"],
  "no-cede": ["accountant.regime.noCede", "NO CEDE"],
  "servicios-personales": [
    "accountant.regime.serviciosPersonales",
    "Serv. personales",
  ],
};

/** i18n `[key, defaultValue]` per legal form. The acronyms carry keys too, so
 *  every displayed string resolves through i18n rather than three of four. */
export const LEGAL_FORM_LABEL: Record<TenantLegalForm, [string, string]> = {
  unipersonal: ["tenantRegime.legalForm.unipersonal", "Unipersonal"],
  sas: ["tenantRegime.legalForm.sas", "SAS"],
  srl: ["tenantRegime.legalForm.srl", "SRL"],
  sa: ["tenantRegime.legalForm.sa", "SA"],
};
